import type { ReportData } from './types'

export type Formatter = ReturnType<typeof formatter>

const MI_PER_KM = 0.621371
const FT_PER_M = 3.28084

/** Unit-aware number formatting for report templates. Each returns null when there's no value. */
export function formatter(units: ReportData['units']) {
  const imperial = units === 'imperial'
  const has = (v: number | null | undefined): v is number => typeof v === 'number' && isFinite(v)

  return {
    imperial,
    distanceUnit: imperial ? 'mi' : 'km',
    elevationUnit: imperial ? 'ft' : 'm',
    speedUnit: imperial ? 'mph' : 'km/h',
    distance(km: number | null | undefined): string | null {
      if (!has(km) || km <= 0) return null
      const v = imperial ? km * MI_PER_KM : km
      return v >= 100 ? v.toFixed(0) : v.toFixed(1)
    },
    elevation(m: number | null | undefined): string | null {
      if (!has(m)) return null
      return Math.round(imperial ? m * FT_PER_M : m).toLocaleString('en-US')
    },
    speed(kmh: number | null | undefined): string | null {
      if (!has(kmh) || kmh <= 0) return null
      return (imperial ? kmh * MI_PER_KM : kmh).toFixed(1)
    },
    /** 1:23:45, or 23:45 under an hour. */
    duration(seconds: number | null | undefined): string | null {
      if (!has(seconds) || seconds <= 0) return null
      const s = Math.round(seconds)
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      const pad = (n: number) => String(n).padStart(2, '0')
      return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
    },
    /** Focus durations: "2:43" min:sec, or "1h 05m" past an hour. */
    focusDuration(seconds: number | null | undefined): string | null {
      if (!has(seconds) || seconds <= 0) return null
      const s = Math.round(seconds)
      if (s >= 3600) return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
    },
    int(v: number | null | undefined): string | null {
      return has(v) ? Math.round(v).toLocaleString('en-US') : null
    },
    fixed(v: number | null | undefined, digits = 1): string | null {
      return has(v) ? v.toFixed(digits) : null
    },
    /** "+14", "−2.3" (true minus sign), "0". */
    signed(v: number | null | undefined, digits = 0): string | null {
      if (!has(v)) return null
      const abs = Math.abs(v).toFixed(digits)
      if (Number(abs) === 0) return digits ? (0).toFixed(digits) : '0'
      return (v > 0 ? '+' : '−') + abs
    },
    date(local: string, style: 'long' | 'short' = 'long'): string {
      // Already local time; parse the parts so the viewer's timezone can't shift the day.
      const [d, t] = local.split('T')
      const [y, mo, day] = d.split('-').map(Number)
      const date = new Date(Date.UTC(y, mo - 1, day))
      const opts: Intl.DateTimeFormatOptions = style === 'long'
        ? { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
        : { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
      return date.toLocaleDateString('en-US', opts)
    },
    ordinal(n: number): string {
      const s = ['th', 'st', 'nd', 'rd']
      const v = n % 100
      return n + (s[(v - 20) % 10] || s[v] || s[0])
    },
  }
}
